import { easeCubicInOut as ease } from 'd3-ease'
import { interpolate } from 'd3-interpolate'
import { scaleLinear } from 'd3-scale'
import { EPSILON } from 'chart-constants'

/*
 * Class to handle some tweening functions for area charts
 */
class StyleTweeners {

  constructor (styles) {
    this.styles = styles
  }

  _stroke (transition, from, to, domain, range = [0, 1]) {
    let interpolateStroke = interpolate(from, to)
      , scale = scaleLinear().domain(domain)
                             .range(range)
                             .clamp(true)
      , path = transition.selection().selectAll('path.line')

    return () => {
      return t => {
        let outT = scale(t) // Scale the ease'd time
        path.style('stroke-width', interpolateStroke(outT))
      }
    }
  }

  _opacity (transition, areaFrom, areaTo, lineFrom, lineTo, domain, fadeLine) {
    let interpolateAreaOpacity = interpolate(areaFrom, areaTo)
      , interpolateLineOpacity = interpolate(lineFrom, lineTo)
      , scale = scaleLinear().domain(domain)
                             .range([0, 1])
                             .clamp(true)
      , selection = transition.selection()

    return () => {
      return t => {
        let _scaled = scale(t)

        selection.selectAll('path.area').style('opacity', interpolateAreaOpacity(_scaled))
        if (fadeLine) {
          selection.selectAll('path.line').style('opacity', interpolateLineOpacity(_scaled))
        }
      }
    }
  }

  adjustStrokeWidth (transition, from, to) {
    let path = transition.selection().selectAll('path.line')
      , interpolateStroke = interpolate(from, to)

    return () => {
      return t => {
        path.style('stroke-width', interpolateStroke(t))
      }
    }
  }

  adjustStroke (transition, from, to) {
    let path = transition.selection().selectAll('path.line')
      , interpolateStroke = interpolate(from, to)

    return () => {
      return t => {
        path.style('stroke', interpolateStroke(t))
      }
    }
  }

  /*
   * Decrease stroke width from the line stroke width to the area stroke width,
   * during the last 25% of the transition
   */
  shrinkStroke (transition) {
    return this._stroke(transition, this.styles.lineStroke, this.styles.areaStroke, [ease(.75), 1])
  }

  /*
   * Increase stroke width from the area stroke width to the line stroke width,
   * during the first 25% of the transition
   */
  growStroke (transition) {
    return this._stroke(transition, this.styles.areaStroke, this.styles.lineStroke, [0, ease(.25)])
  }

  /*
   * Changes the stroke from area width to line width during the first 25% of the transition, then
   * from line width to the area width during the last 75% of the transition
   */
  piecewiseStroke (transition) {
    let domain = [0, ease(.25), ease(.75), 1]
      , range = [0, 1, 1, 0]

    return this._stroke(transition, this.styles.areaStroke, this.styles.lineStroke, domain, range)
  }

  /*
   * Fades out the paths starting immediately and ending at `endAt`, which defaults to 25% of the transition.
   * Pass `fadeLine` true if the line, should also fade out
   */
  fadeOut (transition, fadeLine = false, endAt = .25) {
    let domain = [0, ease(endAt)]
    return this._opacity(transition, this.styles.areaOpacity, EPSILON, 1, EPSILON, domain, fadeLine)
  }

  /*
   * Fade in the paths, starting at `startAt`, which defaults to 75% of the transition.
   * Pass in `fadeLine` true if the line, should also fade in
   */
  fadeIn (transition, fadeLine = false, startAt = .75) {
    let domain = [ease(startAt), 1]
    return this._opacity(transition, EPSILON, this.styles.areaOpacity, EPSILON, 1, domain, fadeLine)
  }
}

export default StyleTweeners
